import { useState } from 'react'
import { Link } from 'react-router-dom'
import { organizations } from '../lib/api'

// PayPal Plan IDs (no trial - we handle trials ourselves)
const PAYPAL_MONTHLY_PLAN_ID = import.meta.env.VITE_PAYPAL_MONTHLY_PLAN_ID || 'P-4PB31959BS8158430NFJKTHQ'
const PAYPAL_YEARLY_PLAN_ID = import.meta.env.VITE_PAYPAL_YEARLY_PLAN_ID || 'P-0KG70841RX736184SNFJKTXY'

export default function TrialExpiredModal({ orgName, orgId, onLogout }) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState(null)

  const handleDeleteData = async () => {
    setDeleting(true)
    setDeleteError(null)
    try {
      await organizations.delete(orgId)
      // After deletion, log out
      onLogout()
    } catch (error) {
      setDeleteError(error.message || 'Failed to delete data')
      setDeleting(false)
    }
  }

  if (showDeleteConfirm) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
          <div className="text-center mb-4">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-1">Delete All Data?</h2>
            <p className="text-sm text-slate-600">
              This will permanently delete <strong>{orgName}</strong> and all associated data.
            </p>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm">
            <p className="font-medium text-red-800 mb-2">This action cannot be undone. You will lose:</p>
            <ul className="text-red-700 space-y-1">
              <li>• All leagues and seasons</li>
              <li>• All teams and player rosters</li>
              <li>• All game schedules and scores</li>
              <li>• All payment records</li>
              <li>• All announcements and settings</li>
            </ul>
          </div>

          {deleteError && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-lg mb-4">
              {deleteError}
            </div>
          )}

          <div className="space-y-2">
            <button
              onClick={handleDeleteData}
              disabled={deleting}
              className="w-full py-2.5 px-4 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {deleting ? 'Deleting...' : 'Yes, Delete Everything'}
            </button>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              disabled={deleting}
              className="w-full py-2 text-sm text-slate-600 hover:text-slate-800"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
        {/* Header - Compact */}
        <div className="text-center mb-4">
          <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-1">Trial Ended</h2>
          <p className="text-sm text-slate-600">
            Subscribe to <strong>{orgName}</strong> to continue
          </p>
        </div>

        {/* Pricing Options - Compact Grid */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <a
            href={`https://www.paypal.com/webapps/billing/plans/subscribe?plan_id=${PAYPAL_MONTHLY_PLAN_ID}`}
            className="block p-3 border-2 border-slate-200 rounded-lg hover:border-ice-400 transition-colors text-center"
          >
            <div className="text-xs text-slate-500 mb-1">Monthly</div>
            <div className="text-xl font-bold text-slate-900">$25<span className="text-sm font-normal">/mo</span></div>
          </a>

          <a
            href={`https://www.paypal.com/webapps/billing/plans/subscribe?plan_id=${PAYPAL_YEARLY_PLAN_ID}`}
            className="block p-3 border-2 border-ice-600 bg-ice-50 rounded-lg hover:bg-ice-100 transition-colors text-center relative"
          >
            <div className="absolute -top-2 right-1">
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Save $60
              </span>
            </div>
            <div className="text-xs text-ice-700 mb-1">Yearly</div>
            <div className="text-xl font-bold text-ice-900">$240<span className="text-sm font-normal">/yr</span></div>
          </a>
        </div>

        {/* 30-day data retention warning */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-xs">
          <div className="flex items-start gap-2">
            <svg className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="font-medium text-amber-800">Your data will be deleted in 30 days</p>
              <p className="text-amber-700 mt-1">Subscribe to keep your leagues, teams, and all data. Or delete your data now if you prefer.</p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-2">
          <Link
            to="/billing"
            className="btn-primary w-full text-center block py-2.5 text-sm"
          >
            Subscribe Now
          </Link>
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex-1 text-center text-xs text-red-600 hover:text-red-700 py-1"
            >
              Delete my data now
            </button>
            <span className="text-slate-300">|</span>
            <button
              onClick={onLogout}
              className="flex-1 text-center text-xs text-slate-500 hover:text-slate-700 py-1"
            >
              Log out
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
